import { type FC, useEffect, useRef, useState } from "react";
import { cn } from "../../../util/classNameConstructor";
import Icon from "../../core/icon/Icon";
import type { IconName } from "../../core/icon/names";

const useChangeTransition = (value?: string | number) => {
  const initial = value !== undefined ? " " : "";
  const currentNumber = useRef<number | string>(initial);
  const [scale, setScale] = useState(1);
  const [displayValue, setDisplayValue] = useState(value || initial);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const duration = 150; // Slightly longer for better reliability

  useEffect(() => {
    if (value === currentNumber.current) return;

    // Clear any existing animation
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    // If this is the first render or no animation is needed, update immediately
    if (currentNumber.current === initial || value === undefined) {
      currentNumber.current = value || initial;
      setDisplayValue(value || initial);
      setScale(1);
      return;
    }

    // Start scale down animation
    setScale(0.3);

    // Use a single timeout for the complete animation cycle
    animationTimeoutRef.current = setTimeout(() => {
      // Update the displayed value and scale back up
      currentNumber.current = value || initial;
      setDisplayValue(value || initial);
      setScale(1);
      animationTimeoutRef.current = null;
    }, duration);

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, [value, initial]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const style = {
    transform: `scale(${scale})`,
    transition: `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    transformOrigin: "center",
    opacity: scale < 0.5 ? 0.7 : 1, // Add opacity transition for smoother effect
  };

  return [style, displayValue] as const;
};
type colors =
  | "neutral"
  | "blue"
  | "orange"
  | "teal"
  | "yellow"
  | "green"
  | "purple"
  | "red"
  | "brown"
  | "rose";
type tones =
  | "1000"
  | "900"
  | "800"
  | "700"
  | "600"
  | "500"
  | "400"
  | "300"
  | "200"
  | "100";

export interface IWithNumberTrueProps {
  selectIcon?: IconName;
  number?: string | number;
  maxNumber?: number;
  color?: `${colors}-${tones}` | "transparent";
  animate?: boolean;
  className?: string;
}

const IconContainer: FC<IWithNumberTrueProps> = ({
  selectIcon,
  number,
  maxNumber,
  animate = true,
  color = "neutral-500",
  className,
}) => {
  const [style, currentNumber] = useChangeTransition(number);
  return (
    <div
      style={{
        backgroundColor:
          color === "transparent"
            ? "transparent"
            : `var(--palette-${color}, var(--palette-neutral-500))`,
      }}
      className={cn(
        "relative flex h-7 min-w-[1.75rem] shrink-0 flex-row items-center justify-between gap-1 rounded-lg px-1.5",
        selectIcon && currentNumber ? "justify-between" : "justify-center",
        !currentNumber && "aspect-square",
        className,
      )}
    >
      {selectIcon ? (
        <div className="flex">
          <Icon name={selectIcon} />
        </div>
      ) : null}
      {currentNumber && (
        <div
          className="text-text-default text-md-de body-md-bold relative text-right tabular-nums leading-none"
          style={{
            minWidth: `${maxNumber?.toString().length || 1}ch`,
            ...(animate ? style : {}),
          }}
        >
          {animate ? currentNumber : number}
        </div>
      )}
    </div>
  );
};

export default IconContainer;
